/**
 * PackPromoModel — pure complete-pack arithmetic for self-rewarding BXGY
 * ──────────────────────────────────────────────────────────────────────
 * Theme-side replica of the v15 checkout Function's pack semantics
 * (beauty-rep-discount-engine …/cart_lines_discounts_generate_run.ts,
 * branch v15). This model has NO monetary authority: the Function prices
 * the cart at checkout; everything here is projection for progress bars,
 * "N more to complete your pack" copy, and PromoCompat's savings-based
 * arbitration. The replica exists so what the banner promises is exactly
 * what the Function computes — including the overlap budget that decides
 * WHICH units would be free.
 *
 * The formula (verdict §2), classifying cart UNITS by the role they can
 * play in one campaign:
 *
 *   A = qualifying \ free      (can only pay)
 *   B = qualifying ∩ free      (can pay or be the gift)
 *   C = free \ qualifying      (can only be the gift)
 *
 *   Qu = |A|+|B|      Fu = |B|+|C|      U = |A|+|B|+|C|
 *
 *   packs         = min( floor(Qu/t), floor(Fu/r), floor(U/(t+r)) )
 *   free units    = packs × r
 *   overlapBudget = Qu − packs × t   (how many gifts may come out of B)
 *
 * The three bounds are exactly the Hall condition for the role assignment,
 * so `packs` is the MAXIMUM number of complete packs and a pack is never
 * emitted partially — either all `r` gifts exist or the pack doesn't.
 *
 * ALL units count, stamped or not: `_marini_q2_free` has no monetary
 * authority under pack semantics and is never read here (invariant I4).
 * Old stamps left in live carts are inert data — the theme must NOT
 * delete them (deleting would lower U and steal the buyer's free unit).
 *
 * Load order: this file is loaded BEFORE promo-compat.js and
 * bxgy-promo.js (layout/theme.liquid) so both read window.PackPromoModel.
 * The same object is exported via module.exports for the Node tests, so
 * the tests run the SHIPPED asset rather than a copy that can drift.
 */
(function () {
  'use strict';

  /* ────────────────────────────────────────
     Family predicate — the Q∩F≠∅ classifier
     ────────────────────────────────────────

     A promo is SELF-REWARDING (pack semantics) when the same product can
     be both the purchase and the gift; otherwise it is DISJOINT (legacy
     v14 semantics). There is deliberately NO `semantics` field anywhere:
     the Function derives the family from
     `config.qualifying_product_ids.some(id => freeSet.has(id))` and the
     theme derives it here from the same set intersection. The shared
     test vector (tests/vectors/bxgy-pack-predicate.json) pins both sides
     so the classifiers cannot diverge.

     Works on any id representation as long as both lists use the same
     one (full GIDs on the Function side, numeric strings on the theme
     side — gidToNumericId is applied by callers before this is invoked).
  ──────────────────────────────────────── */
  function isSelfRewarding(qualifyingIds, freeIds) {
    var free = {};
    (freeIds || []).forEach(function (id) { free[String(id)] = true; });
    return (qualifyingIds || []).some(function (id) { return !!free[String(id)]; });
  }

  /**
   * Fail-closed config shape for self-rewarding campaigns. The complete-
   * pack arithmetic has no defined offer without a positive-integer
   * `reward` and a recognized `pick`, so either missing/malformed fails
   * CLOSED — the Function returns zero discount and the theme hides the
   * campaign (bxgy-promo.js findPromoConfigIssue) / projects zero savings
   * (promo-compat.js). Disjoint campaigns never consult this: absent
   * rewardQuantity means the grandfathered 1:1 there.
   */
  function isValidPackConfig(config) {
    var c = config || {};
    var reward = Number(c.rewardQuantity);
    if (!reward || !isFinite(reward) || reward < 1 || Math.floor(reward) !== reward) return false;
    if (c.pick !== 'cheapest' && c.pick !== 'most_expensive') return false;
    var t = Number(c.threshold);
    if (!t || !isFinite(t) || t < 1 || Math.floor(t) !== t) return false;
    return true;
  }

  /* ────────────────────────────────────────
     Cart-line adaptation (/cart.js shape)
     ────────────────────────────────────────

     /cart.js items carry { key, product_id (number), quantity,
     original_price (cents, pre-discount), price, ... }. We order and sum
     by `original_price`, falling back to `price` — the same convention
     promo-compat.js already used. The Function orders by
     `cost.amountPerQuantity`, which can reflect OTHER discounts and
     therefore flip "cheapest"; that divergence is a known, accepted
     trade-off (verdict §12) — changing the Function's input query would
     have invalidated the 3,024-cart differential corpus.

     Degenerate lines are ignored whole, never thrown on: quantity ≤ 0,
     no product id, or a price that doesn't parse (§4: "línea ignorada,
     nunca excepción"). Absent price keeps the Function's convention of 0.
  ──────────────────────────────────────── */
  function lineUnitPriceCents(item) {
    var raw = item.original_price != null ? item.original_price : item.price;
    if (raw == null) return { ok: true, cents: 0 };
    var n = Number(raw);
    if (!isFinite(n)) return { ok: false, cents: 0 };
    return { ok: true, cents: n };
  }

  /**
   * Classify every cart unit by the roles it can play and collect the
   * free-eligible lines for the allocator. Products in neither set
   * contribute nothing to any counter.
   *
   * `items` are /cart.js line items; `qualifyingIds`/`freeIds` are the
   * campaign's NUMERIC id strings (post-gidToNumericId), matching how
   * `String(item.product_id)` reads. Returns:
   *   { pools: { Qu, Fu, U },
   *     freeEligible: [{ key, productId, quantity, unitPriceCents, cls }] }
   * with cls "B" (dual role) or "C" (gift only). Class-A lines only exist
   * inside pools.Qu — they can never be gifts, so the allocator never
   * sees them.
   */
  function countPackPools(items, qualifyingIds, freeIds) {
    var qualifying = {};
    (qualifyingIds || []).forEach(function (id) { qualifying[String(id)] = true; });
    var free = {};
    (freeIds || []).forEach(function (id) { free[String(id)] = true; });

    var pools = { Qu: 0, Fu: 0, U: 0 };
    var freeEligible = [];

    (items || []).forEach(function (item) {
      if (!item) return;
      var qty = Number(item.quantity);
      if (!isFinite(qty) || qty <= 0) return;
      var productId = item.product_id != null ? String(item.product_id) : null;
      if (!productId) return;

      var isQualifying = !!qualifying[productId];
      var isFree = !!free[productId];
      if (!isQualifying && !isFree) return;

      var price = lineUnitPriceCents(item);
      if (!price.ok) return;

      if (isQualifying) pools.Qu += qty;
      if (isFree) pools.Fu += qty;
      pools.U += qty;

      if (isFree) {
        freeEligible.push({
          key: item.key != null ? String(item.key) : '',
          productId: productId,
          quantity: qty,
          unitPriceCents: price.cents,
          cls: isQualifying ? 'B' : 'C',
        });
      }
    });

    return { pools: pools, freeEligible: freeEligible };
  }

  /**
   * The exact maximum number of COMPLETE packs the cart can hold:
   *
   *   packs×t     ≤ Qu   (enough units that can pay)
   *   packs×r     ≤ Fu   (enough units that can be the gift)
   *   packs×(t+r) ≤ U    (role exclusivity — no unit plays both)
   *
   * The Fu bound is NOT redundant: Q={X,Y}, F={Y}, t=3, r=3 with a 5X+1Y
   * cart has floor(Qu/t)=2 and floor(U/6)=1 but ZERO complete packs,
   * because only one unit can ever be a gift. It is also the bound that
   * stops a promo with no free-eligible line in the cart from inventing
   * an entitlement.
   */
  function maximumCompletePacks(pools, t, r) {
    return Math.min(
      Math.floor(pools.Qu / t),
      Math.floor(pools.Fu / r),
      Math.floor(pools.U / (t + r))
    );
  }

  /**
   * Choose exactly `freeSlots` units to discount, walking the free-
   * eligible units in `pick` order:
   *
   *   cheapest       → unit price ascending
   *   most_expensive → unit price descending
   *   ties           → productId ascending (byte order) → cart line key asc
   *
   * taking C units unconditionally and B units only while `overlapBudget`
   * lasts. The budget is what makes `cheapest` mean "cheapest FEASIBLE":
   * a B unit discounted beyond the budget would leave fewer than packs×t
   * qualifying units paid (invariant I2 — the T-6 leak: 5 qualifying
   * toners + 1 expensive Aloe on most_expensive must discount the Aloe,
   * never a sixth toner).
   *
   * The greedy always fills the slots exactly when the caller computed
   * freeSlots = maximumCompletePacks(...)×r — min(|B|, overlapBudget)+|C|
   * ≥ packs×r follows from the three bounds. Callers treat an unfilled
   * slot as an internal bug and fail closed, never a partial pack.
   * Units are homogeneous within a line, so the per-unit walk collapses
   * to per-line quantities: a line of quantity N yields at most ONE
   * target with quantity ≤ N.
   */
  function allocatePackTargets(freeEligible, freeSlots, overlapBudget, pick) {
    var cheapestFirst = pick === 'cheapest';
    var order = (freeEligible || []).slice().sort(function (a, b) {
      var byPrice = cheapestFirst
        ? a.unitPriceCents - b.unitPriceCents
        : b.unitPriceCents - a.unitPriceCents;
      if (byPrice !== 0) return byPrice;
      if (a.productId !== b.productId) return a.productId < b.productId ? -1 : 1;
      return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
    });

    var targets = [];
    var remaining = freeSlots;
    var budget = overlapBudget;
    for (var i = 0; i < order.length; i++) {
      var line = order[i];
      if (remaining <= 0) break;
      var n;
      if (line.cls === 'C') {
        // Gift-only units are always eligible as the gift.
        n = Math.min(line.quantity, remaining);
      } else {
        // Dual-role units only while the overlap budget lasts: each one
        // taken is a qualifying unit that stops paying.
        n = Math.min(line.quantity, budget, remaining);
        budget -= n;
      }
      if (n <= 0) continue;
      targets.push({
        key: line.key,
        productId: line.productId,
        quantity: n,
        unitPriceCents: line.unitPriceCents,
        cls: line.cls,
      });
      remaining -= n;
    }
    return targets;
  }

  /**
   * One-shot evaluation of a self-rewarding campaign over a /cart.js
   * item list. Returns null when the config fails the closed shape
   * (isValidPackConfig) — mirrors the Function's fail-closed guard, so a
   * misconfigured campaign projects ZERO savings instead of drifting
   * from checkout. Otherwise:
   *
   *   { pools, packs, freeUnits, overlapBudget, targets, savingsCents }
   *
   * packs = 0 means "incomplete pack — no discount at all" (targets []).
   * A non-zero packs whose allocator could not fill every slot is an
   * internal invariant violation; it is reported as packs = 0 (fail
   * closed, like the Function) and flagged via `invariantViolation: true`
   * so tests can assert it never happens.
   */
  function evaluate(items, config) {
    if (!isValidPackConfig(config)) return null;
    var t = Number(config.threshold);
    var r = Number(config.rewardQuantity);
    var pick = config.pick;

    var counted = countPackPools(items, config.qualifyingIds, config.freeIds);
    var packs = maximumCompletePacks(counted.pools, t, r);
    if (packs <= 0) {
      return {
        pools: counted.pools,
        packs: 0,
        freeUnits: 0,
        overlapBudget: 0,
        targets: [],
        savingsCents: 0,
      };
    }

    var freeSlots = packs * r;
    var overlapBudget = counted.pools.Qu - packs * t;
    var targets = allocatePackTargets(counted.freeEligible, freeSlots, overlapBudget, pick);
    var allocated = targets.reduce(function (n, tg) { return n + tg.quantity; }, 0);
    if (allocated !== freeSlots) {
      // INVARIANT VIOLATION: the three bounds guarantee the greedy fills
      // every slot. Reaching this line is an internal bug — fail closed
      // rather than display a partial pack.
      return {
        pools: counted.pools,
        packs: 0,
        freeUnits: 0,
        overlapBudget: 0,
        targets: [],
        savingsCents: 0,
        invariantViolation: true,
      };
    }

    var savingsCents = targets.reduce(function (sum, tg) {
      return sum + tg.quantity * tg.unitPriceCents;
    }, 0);

    return {
      pools: counted.pools,
      packs: packs,
      freeUnits: freeSlots,
      overlapBudget: overlapBudget,
      targets: targets,
      savingsCents: savingsCents,
    };
  }

  /* ────────────────────────────────────────
     Next-pack progress (verdict §6.1)
     ────────────────────────────────────────

     For the NEXT pack K = packs + 1:

       dQ = max(0, K·t     − Qu)   qualifying deficit
       dF = max(0, K·r     − Fu)   gift-eligible deficit
       dU = max(0, K·(t+r) − U)    total-units deficit

       minimumAdditionalUnits = max(dQ, dF, dU)
       progress = min( Qu/(K·t), Fu/(K·r), U/(K·(t+r)) )

     ⚠ The max is only SUFFICIENT when the added units are class B
     (qualifying ∩ free). Copy built from these numbers must name the
     binding class — see buildProgressCopy — or it lies in exactly the
     Aloe/O2 case this design exists for: six free-only units can never
     complete a pack by themselves (gifts don't self-finance).
  ──────────────────────────────────────── */
  function nextPackProgress(pools, t, r, completedPacks) {
    var K = (completedPacks || 0) + 1;
    var dQ = Math.max(0, K * t - pools.Qu);
    var dF = Math.max(0, K * r - pools.Fu);
    var dU = Math.max(0, K * (t + r) - pools.U);
    return {
      nextPack: K,
      dQ: dQ,
      dF: dF,
      dU: dU,
      minimumAdditionalUnits: Math.max(dQ, dF, dU),
      progress: Math.min(pools.Qu / (K * t), pools.Fu / (K * r), pools.U / (K * (t + r))),
    };
  }

  /**
   * The class profile of a campaign's product sets — which copy template
   * is honest for it:
   *   uniform        Q = F (cloths, masks): every participating unit can
   *                  play either role, so "add N participating products"
   *                  is exactly sufficient.
   *   qualifyingOnly-subset  Q ⊂ F strictly (toners): some participating
   *                  units (Aloe/O2) can only be the gift — "at least dQ
   *                  must be qualifying" is mandatory in the copy.
   *   general        every other overlap shape: show the qualifying and
   *                  the free-eligible deficits separately.
   */
  function classProfile(qualifyingIds, freeIds) {
    var q = {};
    (qualifyingIds || []).forEach(function (id) { q[String(id)] = true; });
    var f = {};
    (freeIds || []).forEach(function (id) { f[String(id)] = true; });
    var hasQualifyingOnly = false;
    var hasBoth = false;
    var hasFreeOnly = false;
    Object.keys(q).forEach(function (id) {
      if (f[id]) hasBoth = true; else hasQualifyingOnly = true;
    });
    Object.keys(f).forEach(function (id) {
      if (!q[id]) hasFreeOnly = true;
    });
    var uniform = hasBoth && !hasQualifyingOnly && !hasFreeOnly;
    var qualifyingSubsetOfFree = hasBoth && !hasQualifyingOnly && hasFreeOnly;
    return {
      hasQualifyingOnly: hasQualifyingOnly,
      hasBoth: hasBoth,
      hasFreeOnly: hasFreeOnly,
      uniform: uniform,
      qualifyingSubsetOfFree: qualifyingSubsetOfFree,
    };
  }

  /**
   * Class-aware progress copy for the next pack (verdict §6.1). Pure
   * string builder so every surface (PDP banner, cart card) and the
   * Node tests share the exact wording. Never imperative "free" — the
   * final price is decided at checkout, the banner only informs.
   *
   * Two shapes from ONE builder so they can never drift:
   *   buildProgressCopy — the standalone sentence
   *     ("Add 2 more participating products to complete pack 2 (…). …")
   *   buildProgressTail — everything after the "Add N " lead, for the
   *     Liquid hooks that already render a static `Add ` plus a live
   *     counter span ([data-bxgy-need] / [data-bxgy-pdp-need]).
   *
   * Class templates:
   *   uniform:  "…more participating products to complete pack 2 (…)"
   *   Q ⊂ F:    "… At least 5 must be qualifying items."
   *   general:  "… At least X qualifying and Y free-eligible items are required."
   */
  // CORRECTION. An earlier version of this comment claimed these discounts are
  // computed at checkout and that the cart shows gross. That is FALSE, and the
  // measurement behind it was invalid: the cart it was taken from had no
  // COMPLETE pack, so there was no discount to display — gross was simply the
  // right number.
  //
  // What Shopify actually reports for a 10-cloth cart:
  //   line 2 → original_line_price 11200, final_line_price 0,
  //            allocation 11200 "Buy 1, get 1 free — 2 free", total_discount 11200
  // The Function applies to the CART as a line-level allocation, the drawer
  // renders it (struck price, -100%, subtotal WSP $560 → $280), and it carries
  // through to checkout. So the wording below is accurate but not the whole
  // story: by the time a buyer QUALIFIES, they can already see the discount.
  var AUTO_APPLY_NOTE = ' The discount applies automatically at checkout.';
  var AUTO_APPLY_NOTE_PLURAL = ' Discounts apply automatically at checkout.';

  function buildProgressCopyParts(progress, config, profile) {
    var t = Number(config.threshold);
    var r = Number(config.rewardQuantity);
    var min = progress.minimumAdditionalUnits;
    var noun = min === 1 ? 'product' : 'products';
    var tail = 'more participating ' + noun + ' to complete ' +
      (progress.nextPack === 1 ? 'your first pack' : 'pack ' + progress.nextPack) +
      ' (buy ' + t + ', get ' + r + ' free).';
    var qualifier;
    if (profile.uniform) {
      qualifier = '';
    } else if (profile.qualifyingSubsetOfFree) {
      qualifier = progress.dQ > 0
        ? ' At least ' + progress.dQ + ' must be qualifying items.'
        : '';
    } else {
      var parts = [];
      if (progress.dQ > 0) parts.push(progress.dQ + ' qualifying');
      if (progress.dF > 0) parts.push(progress.dF + ' free-eligible');
      qualifier = parts.length ? ' At least ' + parts.join(' and ') + ' items are required.' : '';
    }
    return {
      min: min,
      // `tail` keeps the reassurance sentence for single-campaign surfaces.
      // `tailShort` omits it so a stack of campaigns can print it ONCE at the
      // end instead of repeating an identical sentence per card — with three
      // live campaigns that repetition was half the text and made the promo
      // block taller than the cart itself.
      tail: tail + qualifier + AUTO_APPLY_NOTE,
      tailShort: tail + qualifier,
    };
  }

  function buildProgressCopy(progress, config, profile) {
    var parts = buildProgressCopyParts(progress, config, profile);
    return 'Add ' + parts.min + ' ' + parts.tail;
  }

  /** "Add N …" without the shared reassurance — see buildProgressTailShort. */
  function buildProgressCopyShort(progress, config, profile) {
    var parts = buildProgressCopyParts(progress, config, profile);
    return 'Add ' + parts.min + ' ' + parts.tailShort;
  }

  function buildProgressTail(progress, config, profile) {
    return buildProgressCopyParts(progress, config, profile).tail;
  }

  /** Same sentence without the shared "applies automatically" reassurance. */
  function buildProgressTailShort(progress, config, profile) {
    return buildProgressCopyParts(progress, config, profile).tailShort;
  }

  /**
   * The reassurance itself, for a surface that prints it once for a stack.
   * Plural when the buyer is looking at several offers at once, since one
   * sentence then covers all of them.
   */
  function autoApplyNote(campaignCount) {
    return (Number(campaignCount) > 1 ? AUTO_APPLY_NOTE_PLURAL : AUTO_APPLY_NOTE).trim();
  }

  var PackPromoModel = {
    isSelfRewarding: isSelfRewarding,
    isValidPackConfig: isValidPackConfig,
    countPackPools: countPackPools,
    maximumCompletePacks: maximumCompletePacks,
    allocatePackTargets: allocatePackTargets,
    evaluate: evaluate,
    nextPackProgress: nextPackProgress,
    classProfile: classProfile,
    buildProgressCopy: buildProgressCopy,
    buildProgressCopyShort: buildProgressCopyShort,
    buildProgressTail: buildProgressTail,
    buildProgressTailShort: buildProgressTailShort,
    autoApplyNote: autoApplyNote,
  };

  if (typeof window !== 'undefined') {
    window.PackPromoModel = PackPromoModel;
  }
  // Exported for the Node tests — same shipped object the storefront
  // reads, so the tests can never drift from production behavior.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = PackPromoModel;
  }
})();
